import {
  Cell,
  Pagination,
  Row,
  Table,
  TableBody,
  TableContainer,
} from '@skupreme/shared/ui';
import type { ReactElement, ReactNode } from 'react';
import type { ISortObject } from '../../legacy-bridge';
import { fromSortDescriptor, toSortDescriptor } from '../../lib/table';

export type CatalogTablePaging = {
  loading: boolean;
  page: number;
  pageSize: number;
  totalData: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  sort: ISortObject;
  onSortChange: (sort: ISortObject) => void;
  noDataMessage: ReactNode;
  /** A line over the table, e.g. how many listings are discontinued. */
  caption?: ReactNode;
};

type CatalogTableFrameProps = CatalogTablePaging & {
  'aria-label': string;
  /** The table's `TableHeader`. */
  header: ReactElement;
  columnCount: number;
  rows: ReactNode;
};

const LOADING_ROWS = 7;

/** The catalog lists' table: card, sorting, loading rows, empty state, pages. */
export function CatalogTableFrame({
  'aria-label': ariaLabel,
  header,
  columnCount,
  rows,
  loading,
  page,
  pageSize,
  totalData,
  onPageChange,
  onPageSizeChange,
  sort,
  onSortChange,
  noDataMessage,
  caption,
}: CatalogTableFrameProps) {
  return (
    <>
      {caption ? (
        <p className="sk:text-dense sk:text-fg-secondary">{caption}</p>
      ) : null}
      {/* Narrower than its columns need, the table scrolls sideways in its own
          card. From xl up it fits, and overflow stays visible so the sticky
          header follows the page scroll. */}
      <TableContainer className="sk:overflow-x-auto sk:rounded-card sk:bg-surface sk:xl:overflow-visible">
        <Table
          aria-label={ariaLabel}
          className="sk:min-w-[56rem] sk:table-fixed"
          sortDescriptor={toSortDescriptor(sort)}
          onSortChange={descriptor =>
            onSortChange(fromSortDescriptor(descriptor, sort))
          }
        >
          {header}
          <TableBody
            renderEmptyState={() => (
              <div className="sk:px-3 sk:py-8 sk:text-center sk:text-fg-tertiary">
                {noDataMessage}
              </div>
            )}
          >
            {loading
              ? Array.from({ length: LOADING_ROWS }, (_, row) => (
                  <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                    <Cell colSpan={columnCount}>
                      <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                    </Cell>
                  </Row>
                ))
              : rows}
          </TableBody>
        </Table>
        {totalData > 0 && (
          <Pagination
            /* Stays in view while the table scrolls sideways under it. */
            className="sk:sticky sk:left-0"
            page={page}
            pageSize={pageSize}
            total={totalData}
            onPageChange={nextPage => {
              onPageChange(nextPage);
              window.scrollTo({ top: 0 });
            }}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </TableContainer>
    </>
  );
}
